import { z } from 'zod';

import type { ChartValueForm } from '@revelio/core/global-ai-assistant/share-display';

import type { AssistantPostingsMode } from '../../CustomDash/components/DataProvider/postings/postings-metric-mode';
import {
  getCurrentPageOptionId as getOptionIdForPath,
  toStandardPageLabel,
} from '../global-ai-assistant-standard-page-match';
import { readChartGeoLevelSwitch } from '../global-ai-geo-taxonomy-switch';
import { chartSummary } from '../turn-outcome-digest';
import { isResolvedPageOfferArtifact } from './artifact-grounding';
import {
  answerAsTextResult,
  renderChartDisplayedResult,
  renderChartFollowUpInstruction,
  renderChartFollowUpPageOption,
  renderChartMissingArtifactResult,
  turnAbortedNoOpResult,
} from './tool-result-copy';
import type { ClientToolDefinition } from './types';

const schema = z.object({ configArtifactId: z.string() }).passthrough();

export const renderChartTool: ClientToolDefinition<typeof schema> = {
  schema,
  async run({ configArtifactId }, { artifacts, deps, signal }) {
    const payload = artifacts.get(configArtifactId) as
      | {
          config: unknown;
          filters?: unknown;
          entityType?: string;
          intentSummary?: string;
          chartSubtitle?: string;
          valueForm?: ChartValueForm;
          standardPagePath?: string;
          answerAsText?: boolean;
          postingsMode?: AssistantPostingsMode;
          geoLevelSwitch?: unknown;
        }
      | undefined;
    if (!payload) {
      return renderChartMissingArtifactResult(configArtifactId);
    }
    // A chart's page link opens that page on the chart's own filters. Filters
    // that resolved nothing would open the page's defaults instead, so such a
    // chart has no page to offer, on either branch below.
    const scopedPagePath = isResolvedPageOfferArtifact(payload.filters)
      ? payload.standardPagePath
      : undefined;
    // Nor may a chart advertise a link to the page already on screen — the
    // page offer cannot even name it.
    const chartPageOptionId = scopedPagePath
      ? getOptionIdForPath(scopedPagePath)
      : undefined;
    const suppressSamePageLink =
      chartPageOptionId !== undefined &&
      chartPageOptionId === deps.getCurrentPageOptionId();
    const pagePath = suppressSamePageLink ? undefined : scopedPagePath;
    // Single-value answer (DP3, headless): NO chart card is shown to the
    // user — the digest comes from the offscreen host, and the model must
    // deliver the figure as plain prose, not narrate "the chart".
    if (payload.answerAsText) {
      const answerDigest = await deps.fetchChartDigest({
        config: payload.config,
        filters: payload.filters,
        entityType: payload.entityType,
        intentSummary: payload.intentSummary,
        chartSubtitle: payload.chartSubtitle,
        valueForm: payload.valueForm,
        postingsMode: payload.postingsMode,
      });
      // The digest layer RESOLVES on abort (synthetic digest, never a
      // rejection) — bail before the offer-context append writes to the
      // transcript of a stopped turn (see ClientToolContext.signal).
      if (signal?.aborted) {
        return turnAbortedNoOpResult;
      }
      const answerSummary = chartSummary(answerDigest);
      // Same error-first contract as the visible path below.
      if (answerSummary.startsWith('ERROR')) {
        return { resultForModel: answerSummary };
      }
      if (pagePath) {
        deps.appendAnswerContextChart({
          config: payload.config,
          filters: payload.filters,
          entityType: payload.entityType,
          intentSummary: payload.intentSummary,
          standardPagePath: pagePath,
        });
      }
      return answerAsTextResult(answerSummary, pagePath);
    }
    const digest = await deps.renderChart(
      pagePath ? payload : { ...payload, standardPagePath: undefined }
    );
    // Same abort contract as the answerAsText branch: the settle wait resolves
    // (synthetic digest) on abort, so bail before doing further work.
    if (signal?.aborted) {
      return turnAbortedNoOpResult;
    }
    const summary = chartSummary(digest);
    // A failed render already reads as ERROR — never tell the model the chart
    // is displayed when it is not.
    if (summary.startsWith('ERROR')) {
      return { resultForModel: summary };
    }
    // The digest describes the DATA, not that the action is finished. Without
    // an explicit completion signal an agentic model reads "here is the chart
    // data" as "now produce the chart" and renders it twice, so the
    // result says plainly that it is on screen.
    //
    // The follow-up markers are computed with the SAME predicates the drawer
    // uses to decide whether the inline trigger can render, so the model never
    // offers a marker that would degrade to plain text.
    const standardPageLabel = pagePath
      ? toStandardPageLabel(pagePath)
      : undefined;
    // Each entry describes an option and names the marker scheme to wrap it
    // in — deliberately NOT written marker-first, so the examples don't fight
    // the reason-first instruction below.
    const followUps: string[] = [];
    if (standardPageLabel) {
      followUps.push(renderChartFollowUpPageOption(standardPageLabel));
    }
    const followUpInstruction = renderChartFollowUpInstruction(
      followUps,
      standardPageLabel
    );
    // The narration is written from this result, a step after
    // generate_chart_config, so a level the user's geography view lacks has
    // to be named here or it is never disclosed.
    const levelNote = readChartGeoLevelSwitch(payload)?.note;
    return renderChartDisplayedResult(
      levelNote ? `${summary}\n${levelNote}` : summary,
      followUpInstruction
    );
  },
};
